import { useState, useEffect } from "react";
import axios from "axios";
import { Shield, Save, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_CRITERIA_TEMPLATE = {
  trusted: {
    max_financial_score: 0.2, max_social_score: 0.15,
    max_legal_score: 0.0, max_identity_score: 0.1,
    max_contradiction_score: 0.1, min_data_completeness: 0.9,
    min_confidence: 0.8, blocker_flags_allowed: 0, warning_flags_allowed: 2,
  },
  limited_trust: {
    max_financial_score: 0.5, max_social_score: 0.4,
    max_legal_score: 0.2, max_identity_score: 0.3,
    max_contradiction_score: 0.3, min_data_completeness: 0.7,
    min_confidence: 0.6, blocker_flags_allowed: 0, warning_flags_allowed: 5,
  },
  unverified: {
    max_financial_score: 0.7, max_social_score: 0.7,
    max_legal_score: 0.5, max_identity_score: 0.6,
    max_contradiction_score: 0.5, min_data_completeness: 0.5,
    min_confidence: 0.4, blocker_flags_allowed: 1, warning_flags_allowed: 10,
  },
};

export default function VettingAdmin() {
  const [criteria, setCriteria] = useState(DEFAULT_CRITERIA_TEMPLATE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [showAudit, setShowAudit] = useState(false);

  useEffect(() => {
    loadCriteria();
  }, []);

  async function loadCriteria() {
    try {
      const res = await axios.get(`${API}/vetting/criteria`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        withCredentials: true,
      });
      if (res.data.criteria) setCriteria(res.data.criteria);
    } catch (e) {
      console.error("Failed to load criteria:", e);
    } finally {
      setLoading(false);
    }
  }

  async function saveCriteria() {
    setSaving(true);
    try {
      await axios.post(`${API}/vetting/criteria`, { config: criteria }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        withCredentials: true,
      });
      toast.success("Criteria saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save criteria");
    } finally {
      setSaving(false);
    }
  }

  async function loadAudit() {
    setShowAudit(!showAudit);
    if (showAudit) return;
    try {
      const res = await axios.get(`${API}/vetting/audit`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        withCredentials: true,
      });
      setAuditLog(res.data.entries || []);
    } catch (e) {
      toast.error("Failed to load audit log");
    }
  }

  function updateThreshold(classification, key, val) {
    setCriteria(prev => ({
      ...prev,
      [classification]: { ...prev[classification], [key]: parseFloat(val) || 0 },
    }));
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const CLASS_NAMES = ["trusted", "limited_trust", "unverified"];
  const CLASS_LABELS = { trusted: "Trusted", limited_trust: "Limited Trust", unverified: "Unverified", flagged: "Flagged (fallthrough)" };
  const FIELD_LABELS = {
    max_financial_score: "Max Financial Score", max_social_score: "Max Social Score",
    max_legal_score: "Max Legal Score", max_identity_score: "Max Identity Score",
    max_contradiction_score: "Max Contradiction Score", min_data_completeness: "Min Data Completeness",
    min_confidence: "Min Confidence", blocker_flags_allowed: "Blocker Flags Allowed",
    warning_flags_allowed: "Warning Flags Allowed",
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
          <Shield className="w-5 h-5 text-purple-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-800 font-['Barlow_Condensed'] tracking-wide">
            VETTING ADMIN
          </h1>
          <p className="text-sm text-slate-500">Classification criteria &amp; audit</p>
        </div>
      </div>

      {/* Criteria Editor */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
        <h2 className="font-semibold text-slate-800 mb-4">Classification Criteria</h2>
        <p className="text-sm text-slate-500 mb-4">
          Thresholds are evaluated in order: Trusted → Limited Trust → Unverified → Flagged.
          A case falls into the first classification that passes all checks.
        </p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-app">
                <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">Threshold</th>
                {CLASS_NAMES.map(cn => (
                  <th key={cn} className="px-3 py-2 text-xs font-semibold text-slate-500 text-center">{CLASS_LABELS[cn]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(FIELD_LABELS).map(([key, label]) => (
                <tr key={key} className="border-t border-slate-100">
                  <td className="px-3 py-2 text-slate-700">{label}</td>
                  {CLASS_NAMES.map(cn => (
                    <td key={cn} className="px-3 py-2 text-center">
                      <input
                        type="number"
                        step="0.01"
                        value={criteria[cn]?.[key] ?? 0}
                        onChange={e => updateThreshold(cn, key, e.target.value)}
                        className="w-20 px-2 py-1 border border-slate-200 rounded text-sm text-center"
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button onClick={saveCriteria} disabled={saving}
          className="mt-4 flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving..." : "Save Criteria"}
        </button>
      </div>

      {/* Audit Log */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <button onClick={loadAudit} className="flex items-center gap-2 text-slate-700 font-semibold mb-4">
          <FileText className="w-5 h-5" />
          Audit Log
          <span className="text-xs text-slate-400 font-normal ml-1">{showAudit ? "(hide)" : "(show)"}</span>
        </button>

        {showAudit && (
          auditLog.length === 0 ? (
            <p className="text-sm text-slate-400">No audit entries found.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {auditLog.map((entry, i) => (
                <div key={i} className="flex items-start gap-3 p-2 text-sm bg-app rounded">
                  <span className="text-xs text-slate-400 font-mono whitespace-nowrap">
                    {new Date(entry.timestamp).toLocaleString()}
                  </span>
                  <span className="text-xs font-medium text-slate-600 bg-slate-200 px-1.5 py-0.5 rounded whitespace-nowrap">
                    {entry.action}
                  </span>
                  <span className="text-xs text-slate-500">{entry.actor}</span>
                  <span className="text-xs text-slate-400">({entry.actor_role})</span>
                  {entry.case_id && (
                    <span className="text-xs text-slate-400 font-mono">case: {entry.case_id.slice(0, 8)}...</span>
                  )}
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}
